/**
 * VÉRAULT Portal — Client · Pre-Trip Readiness (Forms Phase 3)
 *
 * Quiet, editorial reading room of "everything to do before you go."
 * Two stacked sections:
 *   · The personal checklist (interactive — client can check items off)
 *   · The document status (read-only — mirrors what's outstanding)
 *
 * Reveal-gated: the page returns 404-ish state ("Almost ready") unless the
 * advisor has flipped `status` → "revealed".
 */
import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ListChecks,
  ChevronLeft,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  CreditCard,
  Upload,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  PortalButton,
  Empty,
  Skeleton,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const KIND_ICON = {
  form: FileText,
  agreement: FileText,
  payment: CreditCard,
  upload: Upload,
};

const STATUS_TONE = {
  ok: "text-[hsl(168,22%,32%)] bg-[hsl(168,18%,42%)]/10",
  pending: "text-[hsl(var(--foreground-muted))] bg-[hsl(var(--background-paper))]",
  missing: "text-[hsl(var(--accent-terracotta))] bg-[hsl(var(--accent-terracotta))]/10",
  blocking: "text-[hsl(var(--accent-terracotta))] bg-[hsl(var(--accent-terracotta))]/12",
};

const STATUS_LABEL = {
  ok: "Complete",
  pending: "In progress",
  missing: "Needs you",
  blocking: "Action needed",
};

function PersonalItem({ tripId, item, onToggle }) {
  const [busy, setBusy] = React.useState(false);
  const handleToggle = async () => {
    setBusy(true);
    try {
      await api.patch(
        `/client-portal/trips/${tripId}/readiness/items/${item.id}`,
        { done: !item.done },
      );
      onToggle(item.id, !item.done);
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };
  return (
    <li
      className={`flex items-start gap-3 p-3.5 rounded-md border transition-colors ${
        item.done ? "border-[hsl(168,18%,42%)]/30 bg-[hsl(168,18%,42%)]/5" : "border-[rgba(0,0,0,0.05)] bg-white hover:border-[rgba(0,0,0,0.1)]"
      }`}
      data-testid={`readiness-personal-${item.id}`}
    >
      <button
        type="button"
        onClick={handleToggle}
        disabled={busy}
        className={`shrink-0 size-5 rounded-full border-2 flex items-center justify-center mt-0.5 transition-colors ${
          item.done
            ? "border-[hsl(168,22%,32%)] bg-[hsl(168,22%,32%)] text-white"
            : "border-[hsl(var(--accent-gold))]/40 hover:bg-[hsl(var(--accent-gold))]/15 hover:border-[hsl(var(--accent-gold))]"
        }`}
        aria-pressed={item.done}
        data-testid={`readiness-toggle-${item.id}`}
      >
        {item.done && <CheckCircle2 className="size-3" strokeWidth={2.5} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            {item.category}
          </span>
          <span className={`font-serif text-[15px] ${item.done ? "text-[hsl(var(--foreground-muted))] line-through" : "text-[hsl(var(--foreground))]"}`}>
            {item.title}
          </span>
        </div>
        {item.detail && (
          <p className={`mt-1 font-accent italic text-[13px] ${item.done ? "text-[hsl(var(--foreground-muted))]" : "text-[hsl(var(--foreground-secondary))]"}`}>
            {item.detail}
          </p>
        )}
      </div>
    </li>
  );
}

function DocumentItem({ item }) {
  const Icon = KIND_ICON[item.kind] || FileText;
  return (
    <li
      className="flex items-start gap-3 p-3 rounded-md border border-[rgba(0,0,0,0.05)] bg-white"
      data-testid={`readiness-document-${item.id}`}
    >
      <Icon className="size-4 mt-0.5 text-[hsl(var(--foreground-muted))] shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <p className="font-serif text-[14.5px] text-[hsl(var(--foreground))]">{item.label}</p>
        <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">{item.detail}</p>
      </div>
      <span
        className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.18em] ${STATUS_TONE[item.status] || STATUS_TONE.pending}`}
      >
        {STATUS_LABEL[item.status] || item.status}
      </span>
    </li>
  );
}

export default function ClientReadinessPage() {
  const { tripId } = useParams();
  const [data, setData] = React.useState(undefined);

  React.useEffect(() => {
    let alive = true;
    api
      .get(`/client-portal/trips/${tripId}/readiness`)
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData({ readiness: null, trip: null }));
    return () => { alive = false; };
  }, [tripId]);

  const onToggleItem = (id, done) => {
    setData((d) => {
      if (!d?.readiness) return d;
      const items = (d.readiness.personal_checklist?.items || []).map((it) =>
        it.id === id ? { ...it, done } : it,
      );
      return { ...d, readiness: { ...d.readiness, personal_checklist: { items } } };
    });
  };

  if (data === undefined) {
    return <Skeleton className="h-96" />;
  }

  if (!data.readiness) {
    return (
      <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-8" data-testid="readiness-not-revealed">
        <motion.div variants={fadeInUp}>
          <PortalButton as={Link} to={`/portal/client/trips/${tripId}`} variant="ghost" size="sm" icon={<ChevronLeft className="size-3.5" strokeWidth={1.5} />}>
            Back to your trip
          </PortalButton>
        </motion.div>
        <motion.div variants={fadeInUp}>
          <Empty
            title="Almost ready"
            subtitle="Your advisor is preparing a personal pre-trip brief. We'll let you know the moment it's ready."
            icon={<Clock className="size-6" strokeWidth={1.25} />}
          />
        </motion.div>
      </motion.div>
    );
  }

  const personal = data.readiness.personal_checklist?.items || [];
  const docs = data.readiness.document_checklist?.items || [];
  const tripName = data.trip?.internal_trip_name || data.trip?.destinations || "Your trip";

  const doneCount = personal.filter((it) => it.done).length;
  const totalCount = personal.length;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10" data-testid="client-readiness-page">
      <motion.div variants={fadeInUp}>
        <PortalButton as={Link} to={`/portal/client/trips/${tripId}`} variant="ghost" size="sm" icon={<ChevronLeft className="size-3.5" strokeWidth={1.5} />}>
          Back to your trip
        </PortalButton>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Before you go"
          title="Your pre-trip checklist."
          lede={`A quiet readiness brief for ${tripName}. Tick items off as you handle them — your advisor sees what's still pending.`}
        />
      </motion.div>

      {/* Document status */}
      {docs.length > 0 && (
        <motion.div variants={fadeInUp}>
          <EditorialRule label="What we're tracking" />
          <Surface variant="default" padded className="!p-5 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <Heading level="eyebrow">Documents & payments</Heading>
            </div>
            <ul className="space-y-2" data-testid="client-readiness-documents">
              {docs.map((it) => <DocumentItem key={it.id} item={it} />)}
            </ul>
          </Surface>
        </motion.div>
      )}

      {/* Personal checklist — the editorial main act */}
      {personal.length > 0 && (
        <motion.div variants={fadeInUp}>
          <EditorialRule label={`Personal — ${doneCount} of ${totalCount} done`} />
          <Surface variant="default" padded className="!p-5 mt-4">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <Heading level="eyebrow">Pack · prep · know</Heading>
              <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                · curated for you
              </span>
            </div>
            <ul className="space-y-2.5" data-testid="client-readiness-personal">
              {personal.map((it) => (
                <PersonalItem key={it.id} tripId={tripId} item={it} onToggle={onToggleItem} />
              ))}
            </ul>
          </Surface>
        </motion.div>
      )}

      <motion.p variants={fadeInUp} className="text-center font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
        Updates instantly. Your advisor will know what's left.
      </motion.p>
    </motion.div>
  );
}
